/**
 * HOVER A CARD, INSPECT IT — the catalog popup, bound to a single card.
 *
 * Every catalog in the game answers a hover the same way: the card it points at
 * reappears in `#card-hover-preview`, ALIGNED (upright, no resting rotation;
 * it turns and lights under the pointer from the first sample it hears) and
 * ENLARGED, clamped into the viewport and the safe area. `Cards.showCardPreviewFor` already owns that recipe — one
 * shared element, one scale calculation, one stellar path — and this module is
 * only the gesture around it: when to open, when to let go, and what a press on
 * the enlarged card means.
 *
 * It exists because the surfaces that had this behaviour each grew their own
 * copy of it against a GRID (a hovered neighbour, a scrolling column, a stale
 * page of listings), and a screen showing exactly one card needs none of that.
 * The listing screen — the one card in the game that answered a hover with
 * nothing — needs this and nothing more.
 *
 * Touch is the same inspection reached by tapping: `TouchPreview` dispatches a
 * synthetic (untrusted) `mouseenter` on a tap and a synthetic `mouseleave` when
 * the latch is released, so one pair of listeners serves both pointers. What is
 * NOT shared is the teardown: a latch has no pointer to follow, so every
 * pointer-following rule below is armed only for a real hover.
 *
 * Contract: tests/unit/ui/cardHoverPopup.test.js
 */

import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { TouchPreview } from './touch-preview.js';

const POPUP_ID = 'card-hover-preview';

/**
 * `#card-hover-preview` IS SHARED, AND SOMEBODY ELSE MAY WIPE IT.
 *
 * It is one element for the whole app, and several surfaces clear it when they
 * appear so that a stale enlargement cannot outlive the screen it came from. A
 * shared listing link lands on `/play`, where the guest tutorial boots BEHIND
 * the dialog — and the mulligan's own `show()` clears the shared preview. That
 * lands in one of two places: inside the raster wait, where the popup is never
 * built at all, or just after it, where the popup is built and then taken away
 * with no pointer event to notice it. Measured in WebKit, which lost roughly
 * one hover in three to the pair.
 *
 * While the pointer is on the card, the contract is simply that the enlarged
 * card is there, so both paths re-assert it — the first by retrying the open,
 * the second by watching for the removal. BOUNDED, deliberately: a surface that
 * keeps clearing the popup wins after this budget rather than being fought
 * forever, and the budget belongs to one hover.
 */
const OPEN_ATTEMPTS = 3;
const REOPEN_DELAY_MS = 140;
const MAX_RECOVERIES = 3;

function openPopup() {
  if (typeof document === 'undefined') return null;
  return document.getElementById(POPUP_ID);
}

function containsPoint(el, x, y) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return false;
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/**
 * @param {HTMLElement} sourceCard the `.card` element the reader points at
 * @param {object} [options]
 * @param {object} [options.card] the card data the popup is built from; the
 *   binding is inert without it, so a caller whose card failed to render can
 *   attach unconditionally
 * @param {HTMLElement|(() => HTMLElement|null)} [options.scrollHost] what a
 *   wheel over the popup should scroll — the surface behind it
 * @returns {() => void} detach; also dismisses anything this binding opened
 */
export function attachCardHoverPopup(sourceCard, { card, scrollHost = null } = {}) {
  if (!sourceCard || !card || typeof Cards?.showCardPreviewFor !== 'function') return () => {};

  /* One number per HOVER, bumped by every open and every dismissal, so work
     that finishes after the reader has moved on is discarded instead of
     appearing under a pointer that has left. A recovery is the SAME hover and
     keeps the number. */
  let generation = 0;
  /* THE READER IS STILL INSPECTING. Cleared by `dismiss` and by nothing else —
     in particular NOT by the `mouseleave` the card fires when the popup lands
     on top of it. That leave is the popup arriving, not the reader leaving, and
     clearing the flag there made the in-flight open tear its own popup down the
     moment it finished building it: the card then sat under the pointer again,
     re-entered, and rebuilt. */
  let inspecting = false;
  let bound = null;
  let watcher = null;
  let recoveries = 0;
  /* A latch has no pointer to follow. Only a real hover arms the teardown
     rules, so a tap-opened popup is never dismissed by a phone's trailing
     compatibility mouse events. */
  let followsPointer = false;
  /* PUT AWAY ON PURPOSE, AND IT STAYS AWAY.
     The popup is centred on the card it came from, so a press in the middle of
     the enlarged card is a press in the middle of the SOURCE card: removing the
     popup hands the pointer straight back to it, the engine re-enters, and the
     thing the reader just dismissed reappears in the same frame. It comes back
     when they leave the card and return — which is what "I put that away"
     means. */
  let pressLatched = false;

  function unwatch() {
    if (!watcher) return;
    try { watcher.disconnect(); } catch (_) { /* noop */ }
    watcher = null;
  }

  function dismiss() {
    generation += 1;
    inspecting = false;
    followsPointer = false;
    recoveries = 0;
    unwatch();
    if (bound) {
      try { document.removeEventListener('mousemove', onDocumentMove, true); } catch (_) { /* noop */ }
      bound = null;
    }
    try { Cards.clearCardPreview?.(); } catch (_) { /* a popup is presentation */ }
  }

  /**
   * THE POPUP CAN NEVER BE STRANDED.
   *
   * Its own `mouseleave` is the ordinary exit, but a boundary event can be
   * missed — the popup is inserted under a stationary pointer, and engines
   * disagree about what that pointer is then considered to be over. This asks
   * the only question that cannot be missed: is the pointer still on either
   * object? Bound only while a hover-opened popup is up.
   */
  function onDocumentMove(event) {
    if (!bound || !followsPointer || !bound.isConnected) return;
    const previewCard = bound.querySelector('.card') || bound;
    if (containsPoint(previewCard, event.clientX, event.clientY)) return;
    if (containsPoint(sourceCard, event.clientX, event.clientY)) return;
    dismiss();
  }

  /**
   * RELEASED BY LEAVING THE CARD, MEASURED RATHER THAN LISTENED FOR.
   *
   * The obvious release — the source card's own `mouseleave` — can never
   * arrive: at the moment of the press the engine considers the pointer to be
   * over the POPUP, so removing the popup leaves the card with no hover state
   * to leave. WebKit swallowed the release and the card could not be inspected
   * again for the rest of the screen's life. Asking where the pointer actually
   * is cannot be missed by any engine.
   */
  function onLatchMove(event) {
    if (containsPoint(sourceCard, event.clientX, event.clientY)) return;
    releasePressLatch();
  }

  function releasePressLatch() {
    if (!pressLatched) return;
    pressLatched = false;
    try { document.removeEventListener('mousemove', onLatchMove, true); } catch (_) { /* noop */ }
  }

  function dismissByPress() {
    dismiss();
    pressLatched = true;
    try { document.addEventListener('mousemove', onLatchMove, true); } catch (_) { /* noop */ }
  }

  /** Still this hover, and still worth drawing? */
  function current(mine) {
    return mine === generation && inspecting && sourceCard.isConnected;
  }

  /** Someone else took the shared popup away while the reader was reading it. */
  function watchPopup(mine) {
    if (typeof MutationObserver !== 'function' || !document.body) return;
    unwatch();
    watcher = new MutationObserver(() => {
      if (!bound || bound.isConnected) return;
      unwatch();
      bound = null;
      try { document.removeEventListener('mousemove', onDocumentMove, true); } catch (_) { /* noop */ }
      if (!current(mine) || recoveries >= MAX_RECOVERIES) {
        if (current(mine)) dismiss();
        return;
      }
      recoveries += 1;
      void run(mine);
    });
    watcher.observe(document.body, { childList: true });
  }

  function bindPopup(popup, mine) {
    if (!popup || popup === bound) return;
    bound = popup;
    if (followsPointer) {
      popup.addEventListener('mouseleave', dismiss);
      document.addEventListener('mousemove', onDocumentMove, true);
    }
    /* A press on the enlarged card DISMISSES IT. The popup opens over the
       middle of whatever it was opened from, so without an interactive layer
       the press falls through to whatever is underneath — on a modal surface
       that is the backdrop, and the reader loses the screen they were reading
       by clicking the card they were reading it for. */
    popup.addEventListener('click', dismissByPress);
    popup.addEventListener('wheel', (event) => {
      const host = typeof scrollHost === 'function' ? scrollHost() : scrollHost;
      if (host) host.scrollTop += event.deltaY;
    }, { passive: true });
    const previewCard = popup.querySelector('.card');
    /* The enlarged card tilts like the one it came from, from the first
       pointer sample it hears: it opens under the pointer, so it is lit and
       turned towards it straight away rather than after a hold. */
    if (previewCard) {
      try { CardTilt?.registerPreview?.(previewCard); } catch (_) { /* presentation only */ }
    }
    watchPopup(mine);
  }

  async function run(mine) {
    for (let attempt = 0; attempt < OPEN_ATTEMPTS; attempt += 1) {
      let shown = false;
      try {
        shown = await Cards.showCardPreviewFor(sourceCard, card, { interactive: true }) === true;
      } catch (_) {
        shown = false;
      }
      if (!current(mine)) {
        /* Opened for a hover nobody wants any more. A NEWER hover owns the
           popup and must keep it; a finished one leaves nothing behind. */
        if (shown && !inspecting) {
          try { Cards.clearCardPreview?.(); } catch (_) { /* presentation */ }
        }
        return;
      }
      if (shown) {
        bindPopup(openPopup(), mine);
        return;
      }
      await new Promise((resolve) => { setTimeout(resolve, REOPEN_DELAY_MS); });
      if (!current(mine)) return;
    }
    /* Only this hover's own failure retires it — a newer one owns the popup and
       must not be torn down by the loser of the race. */
    if (mine === generation) dismiss();
  }

  function open(event) {
    const trusted = event?.isTrusted !== false;
    /* TouchPreview owns tap inspection and the trailing compatibility-mouse
       window a real tap produces; its own synthetic enter is untrusted. */
    if (trusted && TouchPreview?.isSuppressingMouse?.()) return;
    if (pressLatched) return;
    /* Already showing this card: re-entering must not tear the popup down and
       rebuild it, which costs a raster and reads as a flicker. */
    if (bound?.isConnected) {
      inspecting = true;
      return;
    }
    inspecting = true;
    followsPointer = trusted;
    recoveries = 0;
    void run(++generation);
  }

  function leave(event) {
    /* Moving ONTO the popup is not leaving the card — the popup covers it. */
    const popup = openPopup();
    const to = event?.relatedTarget;
    if (popup && to && (popup === to || popup.contains(to))) return;
    /* A real exit is what releases a press-dismissal. */
    releasePressLatch();
    dismiss();
  }

  TouchPreview?.attach?.(sourceCard);
  sourceCard.addEventListener('mouseenter', open);
  sourceCard.addEventListener('mouseleave', leave);

  return function detach() {
    sourceCard.removeEventListener('mouseenter', open);
    sourceCard.removeEventListener('mouseleave', leave);
    releasePressLatch();
    dismiss();
  };
}
